import axios from 'axios';

/**
 * Serviço para busca de endereços usando a API de geocodificação própria
 * Documentação: API interna do Preço do Gás
 */

// Base URL da API de geocodificação
const BASE_URL = 'https://stream.precodogas.com.br/localizacao/geocode';

/**
 * Busca sugestões de endereços usando a API de geocodificação
 * @param {string} query - Texto digitado pelo usuário
 * @param {Object} options - Opções adicionais de busca
 * @returns {Promise<Array>} - Array de sugestões de endereços
 */
export const searchAddresses = async (query, options = {}) => {
    if (!query || query.length < 3) return { results: [] };

    try {
        const url = `${BASE_URL}/?address=${encodeURIComponent(query)}`;

        const response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (response.data && response.data.success && response.data.data) {
            const results = response.data.data.results || [];
            // Adicionar provider a cada resultado
            const resultsWithProvider = results.map((result, index) => ({
                ...result,
                provider: response.data.data.provider || 'mapbox',
                id: `result-${index}-${result.latitude}-${result.longitude}` // ID único para compatibilidade
            }));

            return { results: resultsWithProvider };
        } else {
            return { results: [] };
        }
    } catch (error) {
        console.error('Erro ao buscar endereços na API de geocodificação:', error);
        throw error;
    }
};

/**
 * Busca detalhes de um endereço usando Place ID ou coordenadas
 * @param {string} id - ID do lugar ou coordenadas
 * @returns {Promise<Object>} - Detalhes do endereço
 */
export const getPlaceDetails = async (id) => {
    // Como a nova API já retorna todos os detalhes na busca,
    // esta função pode retornar null ou buscar novamente se necessário
    console.warn('getPlaceDetails não é mais necessário com a nova API');
    return null;
};

/**
 * Formata o endereço completo para exibição
 * @param {Object} result - Objeto de resultado retornado pela API
 * @returns {string} - Endereço formatado
 */
export const formatAddress = (result) => {
    if (!result) return '';

    // Se já tem formatted_address, usar ele
    if (result.formatted_address) {
        return result.formatted_address;
    }

    // Fallback para formato antigo (compatibilidade)
    const parts = [
        result.logradouro,
        result.numero,
        result.bairro,
        result.cidade,
        result.estado,
        result.cep
    ].filter(Boolean);

    return parts.join(', ');
};

/**
 * Formata o endereço para exibição curta
 * @param {Object} result - Objeto de resultado retornado pela API
 * @returns {string} - Endereço formatado curto
 */
export const formatShortAddress = (result) => {
    if (!result) return '';

    // Formato curto: logradouro, cidade
    const parts = [
        result.logradouro,
        result.cidade
    ].filter(Boolean);

    return parts.join(', ');
};

/**
 * Mapeia o resultado da API para o formato esperado pelo componente
 * @param {Object} result - Um resultado da API
 * @returns {Object} - Objeto formatado para o componente
 */
export const mapResultToPlaceDetails = (result) => {
    if (!result) return null;

    return {
        placeId: result.id || `${result.latitude}-${result.longitude}`,
        formattedAddress: result.formatted_address || formatAddress(result),
        logradouro: result.logradouro || '',
        numero: result.numero || '',
        bairro: result.bairro || '',
        cidade: result.cidade || '',
        estado: result.estado || '',
        cep: result.cep || '',
        latitude: result.latitude,
        longitude: result.longitude,
        provider: result.provider || 'mapbox'
    };
};

/**
 * Obtém a chave da API atual (não aplicável para a nova API)
 * @returns {null} - Sempre retorna null
 */
export const getApiKey = () => {
    return null;
};

export default {
    getApiKey,
    searchAddresses,
    getPlaceDetails,
    formatAddress,
    formatShortAddress,
    mapResultToPlaceDetails
};
